import { CheckCircle2, Circle } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { GroupStage } from '../../redux/types/charterTypes'

const STAGES: { key: GroupStage; label: string }[] = [
  { key: 'opening', label: 'Opening' },
  { key: 'easy_wins', label: 'Easy Wins' },
  { key: 'conflicts', label: 'Conflicts' },
  { key: 'group_sections', label: 'Group Sections' },
  { key: 'gap_review', label: 'Gap Review' },
]

const STAGE_INDEX = STAGES.reduce(
  (acc, s, i) => ({ ...acc, [s.key]: i }),
  {} as Record<GroupStage, number>
)

interface StageProgressBarProps {
  currentStage: GroupStage | null
  amendmentMode?: boolean
  amendingSections?: string[]
  /** When true (charter locked/ratified) every stage renders as done. */
  allComplete?: boolean
}

export function StageProgressBar({
  currentStage,
  amendmentMode = false,
  amendingSections = [],
  allComplete = false,
}: StageProgressBarProps) {
  const currentIdx = currentStage ? (STAGE_INDEX[currentStage] ?? -1) : -1

  return (
    <div className='w-full'>
      {amendmentMode && (
        <div className='mb-3 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 font-medium'>
          Re-negotiation{amendingSections.length > 0 ? `: ${amendingSections.join(', ')}` : ''}
        </div>
      )}

      <div className='space-y-0.5'>
        {STAGES.map((stage, idx) => {
          const isDone = allComplete || currentIdx > idx
          const isActive = !allComplete && currentIdx === idx

          return (
            <div
              key={stage.key}
              className={cn(
                'flex items-center gap-2.5 px-2 py-1.5 rounded-md transition-colors',
                isActive && 'bg-indigo-50 border-l-2 border-indigo-500 pl-1.5'
              )}
            >
              {isDone ? (
                <CheckCircle2 className='h-4 w-4 text-emerald-500 flex-shrink-0' />
              ) : isActive ? (
                <div className='h-4 w-4 rounded-full bg-indigo-600 flex-shrink-0 flex items-center justify-center'>
                  <div className='h-1.5 w-1.5 rounded-full bg-white' />
                </div>
              ) : (
                <Circle className='h-4 w-4 text-gray-300 flex-shrink-0' />
              )}

              <span
                className={cn(
                  'text-xs',
                  isDone && 'text-emerald-600 line-through decoration-emerald-300',
                  isActive && 'text-indigo-700 font-semibold',
                  !isDone && !isActive && 'text-gray-400'
                )}
              >
                {stage.label}
              </span>

              {isActive && (
                <span className='ml-auto text-[10px] font-medium text-indigo-500 bg-indigo-100 px-1.5 py-0.5 rounded-full whitespace-nowrap'>
                  Current
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
